/* eslint-disable no-console */
import axios from "axios";
import { getToken } from "../auth-helper";
import { AppConfig } from "../config/app-config";
import {
  SortTypeData,
  SourceTypeData,
  UserGroupData,
  FileTypeData,
  SearchConditionData,
} from "../../types/documents";
import { isPropertyOfSourceTypeData } from "../typeguard/sourcetype-typeguard";
import { isPropertyOfFileTypeData } from "../typeguard/filetype-typeguard";

// ユーザーグループIDをURLパラメータ形式に変換・追加する
function appendUserGroupIdParams(
  params: URLSearchParams | FormData,
  userGroupData?: UserGroupData
) {
  if (userGroupData) {
    Object.keys(userGroupData).forEach(key => {
      if (userGroupData[key]) {
        const id = key.split("-")[1];
        params.append("user_group_ids", id);
      }
    });
  }
}

// 取得元タイプをURLパラメータ形式に変換・追加する
function appendSourceTypeParams(
  params: URLSearchParams | FormData,
  sourceTypeData?: SourceTypeData
) {
  if (sourceTypeData) {
    Object.keys(sourceTypeData).forEach(key => {
      if (isPropertyOfSourceTypeData(key)) {
        if (sourceTypeData[key]) {
          params.append("source_types", String(key).toUpperCase());
        }
      }
    });
  }
}

// ファイルタイプをURLパラメータ形式に変換・追加する
function appendFileTypeParams(
  params: URLSearchParams | FormData,
  fileTypeData?: FileTypeData
) {
  if (fileTypeData) {
    Object.keys(fileTypeData).forEach(key => {
      if (isPropertyOfFileTypeData(key)) {
        if (fileTypeData[key]) {
          params.append("file_types", key);
        }
      }
    });
  }
}

// 日付の文字列化
function dateToStr(date: Date) {
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();
  const dateStr = `${y}/${m}/${d}`;
  return dateStr;
}

// 日付指定(簡易)の日付範囲値作成
function createDateRange(dateValue: string) {
  const today = new Date();
  const dateVal = new Date();
  if (dateValue === "11") {
    // １週間以内
    dateVal.setDate(today.getDate() - 7);
  } else if (dateValue === "12") {
    // １か月以内
    dateVal.setMonth(today.getMonth() - 1);
  } else if (dateValue === "13") {
    // ３か月以内
    dateVal.setMonth(today.getMonth() - 3);
  } else if (dateValue === "14") {
    // １年以内
    dateVal.setFullYear(today.getFullYear() - 1);
  }

  return { fromDate: dateVal, toDate: today };
}

// 項目をURLパラメータに設定(共通)
function PropsToParams(
  params: URLSearchParams | FormData,
  condition: SearchConditionData,
  sort: SortTypeData,
  page: number
) {
  // ユーザーグループID
  appendUserGroupIdParams(params, condition.userGroupData);
  // 取得元
  appendSourceTypeParams(params, condition.sourceTypeData);
  // ファイル種類
  appendFileTypeParams(params, condition.fileTypeData);

  if (condition.dateTypeData.dateType) {
    if (!condition.dateTypeData.dateDetailMode) {
      // 日付指定(簡易)
      if (condition.dateTypeData.dateValue) {
        const dataRange = createDateRange(condition.dateTypeData.dateValue);
        if (condition.dateTypeData.dateType === "10") {
          params.append("create_date_gte", dateToStr(dataRange.fromDate));
          params.append("create_date_lte", dateToStr(dataRange.toDate));
        } else if (condition.dateTypeData.dateType === "20") {
          params.append("update_date_gte", dateToStr(dataRange.fromDate));
          params.append("update_date_lte", dateToStr(dataRange.toDate));
        }
      }
    } else if (
      condition.dateTypeData.selectedFromDate &&
      condition.dateTypeData.selectedToDate
    ) {
      // 日付指定(詳細)
      if (condition.dateTypeData.dateType === "10") {
        params.append(
          "create_date_gte",
          dateToStr(condition.dateTypeData.selectedFromDate)
        );
        params.append(
          "create_date_lte",
          dateToStr(condition.dateTypeData.selectedToDate)
        );
      } else if (condition.dateTypeData.dateType === "20") {
        params.append(
          "update_date_gte",
          dateToStr(condition.dateTypeData.selectedFromDate)
        );
        params.append(
          "update_date_lte",
          dateToStr(condition.dateTypeData.selectedToDate)
        );
      }
    }
  }

  if ("item" in sort && sort.item && "order" in sort && sort.order) {
    params.append("sort_item", sort.item);
    params.append("sort_order", String(sort.order));
  }

  if (page) {
    params.append("page", String(page));
  }
}

// 全文検索の項目をURLパラメータに設定
function PropsToParamsFTSearch(
  text: string,
  condition: SearchConditionData,
  sort: SortTypeData,
  page: number
) {
  const params = new URLSearchParams();
  PropsToParams(params, condition, sort, page);
  if (text) {
    params.append("text", text);
  }
  return params;
}

// 類似文書検索(ファイル)の項目をURLパラメータに設定
function PropsToParamsSimiralyTextSearchByFile(
  file: File,
  condition: SearchConditionData,
  sort: SortTypeData,
  isUserHistory: boolean,
  page: number
) {
  const params = new FormData();
  PropsToParams(params, condition, sort, page);
  if (file) {
    params.append("file", file);
  }
  if (isUserHistory) {
    params.append("is_user_history", String(isUserHistory));
  }
  return params;
}

// 類似文書検索(文書)の項目をURLパラメータに設定
function PropsToParamsSimiralyTextSearchByText(
  text: string,
  condition: SearchConditionData,
  sort: SortTypeData,
  isUserHistory: boolean,
  page: number
) {
  const params = new URLSearchParams();
  PropsToParams(params, condition, sort, page);
  // console.log(props);
  if (text) {
    params.append("text", text);
  }
  if (isUserHistory) {
    params.append("is_user_history", String(isUserHistory));
  }
  return params;
}

// 類似文書検索(文書ID)の項目をURLパラメータに設定
function PropsToParamsSimiralyTextSearchById(
  docId: string,
  condition: SearchConditionData,
  sort: SortTypeData,
  isUserHistory: boolean,
  page: number
) {
  const params = new URLSearchParams();
  PropsToParams(params, condition, sort, page);
  if (docId) {
    params.append("doc_id", docId);
  }
  if (isUserHistory) {
    params.append("is_user_history", String(isUserHistory));
  }
  return params;
}

export const DocumentsApi = {
  // 詳細情報取得
  detail: (docId: string) => {
    const apiBase = AppConfig.get("apiBase");
    // console.log(`BaseApi apiBase: ${apiBase}`);
    const token = getToken();
    // console.log(`BaseApi token: ${token}`);

    const params = new URLSearchParams();
    params.append("doc_id", docId);

    return axios.get(`${apiBase}/documents/details`, {
      headers: { Authorization: `JWT ${token}` },
      params,
    });
  },

  // 全文検索
  fullTextSearch: (
    text: string,
    condition: SearchConditionData,
    sort: SortTypeData,
    page: number
  ) => {
    const apiBase = AppConfig.get("apiBase");
    // console.log(`BaseApi apiBase: ${apiBase}`);
    const token = getToken();
    // console.log(`BaseApi token: ${token}`);

    const params = PropsToParamsFTSearch(text, condition, sort, page);

    return axios.get(`${apiBase}/documents/full_text_search`, {
      headers: { Authorization: `JWT ${token}` },
      params,
    });
  },

  // 類似文書検索(ファイル)
  similarityTextSearchByFile: (
    file: File,
    condition: SearchConditionData,
    sort: SortTypeData,
    isUserHistory: boolean,
    page: number
  ) => {
    const apiBase = AppConfig.get("apiBase");
    // console.log(`BaseApi apiBase: ${apiBase}`);
    const token = getToken();
    // console.log(`BaseApi token: ${token}`);

    const params = PropsToParamsSimiralyTextSearchByFile(
      file,
      condition,
      sort,
      isUserHistory,
      page
    );
    return axios.post(
      `${apiBase}/documents/similarity_text_search_by_file/`,
      params,
      {
        headers: {
          Authorization: `JWT ${token}`,
          "content-type": "multipart/form-data",
        },
      }
    );
  },

  // 類似文書検索(文書)
  similarityTextSearchByText: (
    text: string,
    condition: SearchConditionData,
    sort: SortTypeData,
    isUserHistory: boolean,
    page: number
  ) => {
    const apiBase = AppConfig.get("apiBase");
    // console.log(`BaseApi apiBase: ${apiBase}`);
    const token = getToken();
    // console.log(`BaseApi token: ${token}`);

    const params = PropsToParamsSimiralyTextSearchByText(
      text,
      condition,
      sort,
      isUserHistory,
      page
    );

    return axios.get(`${apiBase}/documents/similarity_text_search_by_text`, {
      headers: { Authorization: `JWT ${token}` },
      params,
    });
  },

  // 類似文書検索(文書)
  similarityTextSearchById: (
    docId: string,
    condition: SearchConditionData,
    sort: SortTypeData,
    isUserHistory: boolean,
    page: number
  ) => {
    const apiBase = AppConfig.get("apiBase");
    // console.log(`BaseApi apiBase: ${apiBase}`);
    const token = getToken();
    // console.log(`BaseApi token: ${token}`);

    const params = PropsToParamsSimiralyTextSearchById(
      docId,
      condition,
      sort,
      isUserHistory,
      page
    );

    return axios.get(`${apiBase}/documents/similarity_text_search_by_id`, {
      headers: { Authorization: `JWT ${token}` },
      params,
    });
  },

  // 重要語検索
  keyWordsSearch: (
    keywords: string[],
    userGroupIds: number[],
    excludewords: string[],
    page: number
  ) => {
    const apiBase = AppConfig.get("apiBase");
    // console.log(`BaseApi apiBase: ${apiBase}`);
    const token = getToken();
    // console.log(`BaseApi token: ${token}`);

    const params = new URLSearchParams();
    keywords.forEach(word => {
      params.append("keywords", word);
    });
    userGroupIds.forEach(userGroupId => {
      params.append("user_group_ids", userGroupId.toString());
    })
    excludewords.forEach(word => {
      params.append("excludewords", word);
    });
    params.append("page", page.toString());

    return axios.get(`${apiBase}/documents/key_words_search`, {
      headers: { Authorization: `JWT ${token}` },
      params,
    });
  },
};
